if (!customElements.get('video-carousel')) {
  const VISIBLE_RATIO = 0.6;

  customElements.define(
    'video-carousel',
    class VideoCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.video-carousel__track');
        this.prevButton = this.querySelector('.video-carousel__arrow--prev');
        this.nextButton = this.querySelector('.video-carousel__arrow--next');
        this.slides = Array.from(this.querySelectorAll('.video-carousel__slide'));
        if (!this.track || !this.slides.length) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const saveData = Boolean(navigator.connection && navigator.connection.saveData);
        this.canAutoplay = !reducedMotion && !saveData;
        this.userPaused = new WeakSet();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.slides.forEach((slide) => this.bindSlide(slide, signal));

        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.scrollByDirection(-1), { signal });
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.scrollByDirection(1), { signal });
        }
        this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });
        window.addEventListener('resize', () => this.updateArrows(), { signal });
        this.updateArrows();

        this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
          threshold: VISIBLE_RATIO,
        });
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      disconnectedCallback() {
        if (this.abortController) this.abortController.abort();
        if (this.observer) this.observer.disconnect();
      }

      bindSlide(slide, signal) {
        const video = slide.querySelector('video');
        const toggle = slide.querySelector('.video-carousel__toggle');
        const sound = slide.querySelector('.video-carousel__sound');
        if (!video) return;

        video.addEventListener('play', () => this.setPlayingState(slide, true), { signal });
        video.addEventListener('pause', () => this.setPlayingState(slide, false), { signal });

        if (toggle) {
          toggle.addEventListener(
            'click',
            () => {
              if (video.paused) {
                this.userPaused.delete(video);
                this.play(video);
              } else {
                this.userPaused.add(video);
                video.pause();
              }
            },
            { signal }
          );
        }

        if (sound) {
          sound.addEventListener(
            'click',
            () => {
              const turnOn = video.muted;
              this.slides.forEach((other) => this.setSound(other, false));
              if (turnOn) {
                this.setSound(slide, true);
                this.userPaused.delete(video);
                this.play(video);
              }
            },
            { signal }
          );
        }
      }

      setPlayingState(slide, isPlaying) {
        const toggle = slide.querySelector('.video-carousel__toggle');
        slide.classList.toggle('is-playing', isPlaying);
        if (toggle) {
          toggle.setAttribute('aria-label', isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
        }
      }

      setSound(slide, isOn) {
        const video = slide.querySelector('video');
        const sound = slide.querySelector('.video-carousel__sound');
        if (video) video.muted = !isOn;
        if (sound) {
          sound.setAttribute('aria-pressed', String(isOn));
          sound.setAttribute('aria-label', isOn ? sound.dataset.labelMute : sound.dataset.labelUnmute);
        }
      }

      play(video) {
        const attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;

          if (entry.intersectionRatio < VISIBLE_RATIO) {
            video.pause();
            this.setSound(entry.target, false);
          } else if (this.canAutoplay && !this.userPaused.has(video)) {
            this.play(video);
          }
        });
      }

      scrollByDirection(direction) {
        const first = this.slides[0];
        const second = this.slides[1];
        const step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
        this.track.scrollBy({ left: step * direction });
      }

      updateArrows() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        if (this.prevButton) this.prevButton.hidden = this.track.scrollLeft <= 4;
        if (this.nextButton) this.nextButton.hidden = this.track.scrollLeft >= maxScroll - 4;
      }
    }
  );
}
